// OG surface registry — single source of truth mapping every
// `ShareSurface` kind to its OG image route, cache class, shell variant,
// and i18n alt-text key.
//
// ─────────────────────────────────────────────────────────────────
// HOW TO ADD AN OG IMAGE FOR A SURFACE
// ─────────────────────────────────────────────────────────────────
//   1. Implement the route at `app/api/og/<surface>/[id]/route.tsx`
//      using `<OgShell variant=…>` + primitives + `renderOg()`.
//   2. Flip the `buildPath` for that surface kind below from
//      `() => null` to a real path builder.
//   3. Add or update `og.alt.<surface>` in `src/i18n/locales/en.json`
//      and run `pnpm i18n:sync`.
//   4. The page that owns the URL adds `buildOgMetadata(...)` to its
//      `generateMetadata`.
//
// Surfaces with `buildPath: () => null` fall back to `/api/og/default` —
// no surface ever 404s when shared.
//
// The mapped type below produces a TS error if a new `kind` is added
// to `ShareSurface` and not registered here. Exhaustiveness is
// compile-time guaranteed, with a runtime test as belt-and-braces.

import type { ShareSurface } from '@/lib/share/events';

import type { CacheClass } from './cache';

export type OgShellVariant = 'default' | 'celebration' | 'data' | 'mono' | 'gift';

// Market/proposal routes also use immutable caching after reaching a terminal state.
const OUTCOME_PALETTE_VERSION = 'market-1';

export interface OgSurfaceDescriptor<S extends ShareSurface = ShareSurface> {
  /** Discriminant matching `ShareSurface['kind']`. */
  kind: S['kind'];
  /** Build the OG image path for this surface. Returns `null` when the
   *  surface has no specialized OG yet — the caller falls back to
   *  `/api/og/default`. */
  buildPath: (surface: S) => string | null;
  /** Cache class. `renderOg()` applies the matching `Cache-Control`. */
  cache: CacheClass;
  /** Shell variant. Override at render time only for data-driven cases. */
  shellVariant: OgShellVariant;
  /** i18n key resolved at metadata-build time for `og:image:alt`. */
  altKey: string;
}

/** Mapped type ensures every `ShareSurface['kind']` has a registry
 *  entry at compile time. */
export const OG_SURFACE_REGISTRY: {
  [K in ShareSurface['kind']]: OgSurfaceDescriptor<Extract<ShareSurface, { kind: K }>>;
} = {
  market: {
    kind: 'market',
    buildPath: (s: Extract<ShareSurface, { kind: 'market' }>): string =>
      `/api/og/market/${s.marketPubkey}?palette=${OUTCOME_PALETTE_VERSION}`,
    cache: 'live',
    shellVariant: 'data',
    altKey: 'og.alt.market',
  },
  live_event: {
    kind: 'live_event',
    buildPath: (s: Extract<ShareSurface, { kind: 'live_event' }>): string | null => {
      if (s.eventId.length === 0 || s.title.length === 0 || s.bannerImageUrl.length === 0)
        return null;
      const query = new URLSearchParams({
        title: s.title,
        image: s.bannerImageUrl,
        phase: s.phase,
        category: s.category,
        schedule: s.scheduleLabel,
      });
      for (const imageUrl of s.identityImageUrls.slice(0, 2)) query.append('identity', imageUrl);
      return `/api/og/live_event/${encodeURIComponent(s.eventId)}?${query.toString()}`;
    },
    cache: 'steady',
    shellVariant: 'data',
    altKey: 'og.alt.live_event',
  },
  competition: {
    kind: 'competition',
    // Display snapshot in the query, like `live_event`: the route renders
    // without a backend read and each snapshot is its own cache key.
    buildPath: (s: Extract<ShareSurface, { kind: 'competition' }>): string | null => {
      if (s.competitionId.length === 0 || s.title.length === 0) return null;
      const query = new URLSearchParams({
        v: '3',
        title: s.title,
        phase: s.phase,
        prize: s.prizeLabel,
        end: s.endLabel,
      });
      return `/api/og/competition/${encodeURIComponent(s.competitionId)}?${query.toString()}`;
    },
    cache: 'steady',
    shellVariant: 'data',
    altKey: 'og.alt.competition',
  },
  competition_rank: {
    kind: 'competition_rank',
    // Standing snapshot in the query, like `rewards_rank`, so the unfurl shows
    // the rank and predicted winnings the user held at share time.
    buildPath: (s: Extract<ShareSurface, { kind: 'competition_rank' }>): string | null => {
      if (s.competitionId.length === 0 || s.userId.length === 0 || s.rank < 1) return null;
      const query = new URLSearchParams({
        u: s.userId,
        rank: s.rank.toString(),
        title: s.title,
        value: s.valueLabel,
        board: s.rankedByLabel,
        prize: s.prizeLabel,
        phase: s.phase,
      });
      if (s.winningsLabel !== null) query.set('winnings', s.winningsLabel);
      if (s.handle !== null) query.set('handle', s.handle);
      if (s.displayName !== null) query.set('name', s.displayName);
      if (s.avatarUrl !== null) query.set('avatar', s.avatarUrl);
      return `/api/og/competition_rank/${encodeURIComponent(s.competitionId)}?${query.toString()}`;
    },
    cache: 'live',
    shellVariant: 'celebration',
    altKey: 'og.alt.competition_rank',
  },
  winner: {
    kind: 'winner',
    buildPath: (s: Extract<ShareSurface, { kind: 'winner' }>): string => `/api/og/winner/${s.mint}`,
    // Token prices and trading stats keep changing after graduation.
    cache: 'live',
    shellVariant: 'default',
    altKey: 'og.alt.winner',
  },
  proposal: {
    kind: 'proposal',
    buildPath: (s: Extract<ShareSurface, { kind: 'proposal' }>): string =>
      `/api/og/proposal/${s.proposalPubkey}?palette=${OUTCOME_PALETTE_VERSION}`,
    cache: 'live',
    shellVariant: 'data',
    altKey: 'og.alt.proposal',
  },
  gift: {
    kind: 'gift',
    buildPath: (s: Extract<ShareSurface, { kind: 'gift' }>): string | null =>
      s.opaqueId !== undefined ? `/api/og/gift/${s.opaqueId}` : null,
    cache: 'steady',
    shellVariant: 'gift',
    altKey: 'og.alt.gift',
  },
  gift_thank: {
    kind: 'gift_thank',
    buildPath: (_s: Extract<ShareSurface, { kind: 'gift_thank' }>): string => '/api/og/gift_thank',
    cache: 'static',
    shellVariant: 'celebration',
    altKey: 'og.alt.gift_thank',
  },
  support_mainnet: {
    kind: 'support_mainnet',
    buildPath: (_s: Extract<ShareSurface, { kind: 'support_mainnet' }>): string =>
      '/api/og/support_mainnet',
    cache: 'static',
    shellVariant: 'mono',
    altKey: 'og.alt.support_mainnet',
  },
  referral_link: {
    kind: 'referral_link',
    buildPath: (s: Extract<ShareSurface, { kind: 'referral_link' }>): string | null =>
      s.userId.length > 0 ? `/api/og/referral/${s.userId}` : null,
    cache: 'steady',
    shellVariant: 'celebration',
    altKey: 'og.alt.referral',
  },
  referral_banner: {
    kind: 'referral_banner',
    buildPath: (s: Extract<ShareSurface, { kind: 'referral_banner' }>): string | null =>
      s.userId.length > 0 ? `/api/og/referral/${s.userId}` : null,
    cache: 'steady',
    shellVariant: 'celebration',
    altKey: 'og.alt.referral',
  },
  thesis: {
    kind: 'thesis',
    buildPath: (s: Extract<ShareSurface, { kind: 'thesis' }>): string | null =>
      `/api/og/thesis/${s.signature}?palette=${OUTCOME_PALETTE_VERSION}`,
    cache: 'immutable',
    shellVariant: 'data',
    altKey: 'og.alt.thesis',
  },
  trade: {
    kind: 'trade',
    // The trade OG route needs the full trade context to render. Since
    // there's no `getTrade(signature)` SDK endpoint, we encode context
    // as query params on the OG path. The route reads them, then fetches
    // only `marketTitle` + outcome label + user identity by pubkey.
    buildPath: (s: Extract<ShareSurface, { kind: 'trade' }>): string | null => {
      if (s.signature.length === 0 || s.marketPubkey.length === 0 || s.userPubkey.length === 0) {
        return null;
      }
      const qs = new URLSearchParams({
        m: s.marketPubkey,
        u: s.userPubkey,
        d: s.direction,
        a: s.amountD6,
        o: s.outcomeIndex.toString(),
        palette: OUTCOME_PALETTE_VERSION,
      });
      return `/api/og/trade/${s.signature}?${qs.toString()}`;
    },
    cache: 'immutable',
    shellVariant: 'celebration',
    altKey: 'og.alt.trade',
  },
  boost: {
    kind: 'boost',
    buildPath: (s: Extract<ShareSurface, { kind: 'boost' }>): string | null =>
      s.marketPubkey.length > 0
        ? `/api/og/market/${s.marketPubkey}?palette=${OUTCOME_PALETTE_VERSION}`
        : null,
    cache: 'live',
    shellVariant: 'data',
    altKey: 'og.alt.market',
  },
  proposal_back: {
    kind: 'proposal_back',
    // Same query-param strategy as `trade` — the OG route reads context
    // from `?u=&a=&o=&v=` and fetches only what those params can't express
    // (proposal title + backer identity). `o` remains analytics-only.
    buildPath: (s: Extract<ShareSurface, { kind: 'proposal_back' }>): string | null => {
      if (s.proposalPubkey.length === 0 || s.userPubkey.length === 0) return null;
      const qs = new URLSearchParams({
        u: s.userPubkey,
        a: s.amountD6,
        o: s.outcomeIndex.toString(),
        v: s.displayAmount,
      });
      return `/api/og/proposal_back/${s.proposalPubkey}?${qs.toString()}`;
    },
    cache: 'immutable',
    shellVariant: 'celebration',
    altKey: 'og.alt.proposal_back',
  },
  rank: {
    kind: 'rank',
    buildPath: (s: Extract<ShareSurface, { kind: 'rank' }>): string | null =>
      s.userId.length > 0 ? `/api/og/rank/${s.userId}` : null,
    cache: 'steady',
    shellVariant: 'celebration',
    altKey: 'og.alt.rank',
  },
  rank_boosters: {
    kind: 'rank_boosters',
    buildPath: (s: Extract<ShareSurface, { kind: 'rank_boosters' }>): string | null =>
      s.userId.length > 0 ? `/api/og/rank-boosters/${s.userId}` : null,
    cache: 'steady',
    shellVariant: 'celebration',
    altKey: 'og.alt.rank_boosters',
  },
  rank_creators: {
    kind: 'rank_creators',
    buildPath: (s: Extract<ShareSurface, { kind: 'rank_creators' }>): string | null =>
      s.userId.length > 0 ? `/api/og/rank-creators/${s.userId}` : null,
    cache: 'steady',
    shellVariant: 'celebration',
    altKey: 'og.alt.rank_creators',
  },
  rank_backers: {
    kind: 'rank_backers',
    buildPath: (s: Extract<ShareSurface, { kind: 'rank_backers' }>): string | null =>
      s.userId.length > 0 ? `/api/og/rank-backers/${s.userId}` : null,
    cache: 'steady',
    shellVariant: 'celebration',
    altKey: 'og.alt.rank_backers',
  },
  profile: {
    kind: 'profile',
    buildPath: (s: Extract<ShareSurface, { kind: 'profile' }>): string =>
      `/api/og/profile/${s.userId}`,
    cache: 'steady',
    shellVariant: 'data',
    altKey: 'og.alt.profile',
  },
  backer_rewards: {
    kind: 'backer_rewards',
    buildPath: (s: Extract<ShareSurface, { kind: 'backer_rewards' }>): string =>
      `/api/og/backer_rewards/${encodeURIComponent(s.userId)}`,
    cache: 'live',
    shellVariant: 'celebration',
    altKey: 'og.alt.backer_rewards',
  },
  streak: {
    kind: 'streak',
    // Streak card fetches the user's current/longest streak by userId.
    // `live` cadence: the count advances daily (and intra-day when the
    // user makes their first trade of the day), so it must not cache hard.
    buildPath: (s: Extract<ShareSurface, { kind: 'streak' }>): string | null =>
      s.userId.length > 0 ? `/api/og/streak/${s.userId}` : null,
    cache: 'live',
    shellVariant: 'celebration',
    altKey: 'og.alt.streak',
  },
  globe: {
    kind: 'globe',
    buildPath: (s: Extract<ShareSurface, { kind: 'globe' }>): string => `/api/og/globe/${s.mode}`,
    cache: 'steady',
    shellVariant: 'data',
    altKey: 'og.alt.globe',
  },
  pnl_position: {
    kind: 'pnl_position',
    buildPath: (s: Extract<ShareSurface, { kind: 'pnl_position' }>): string | null => {
      if (s.marketPubkey.length === 0 || s.walletPubkey.length === 0) return null;
      const path = `/api/og/pnl/${s.marketPubkey}/${s.walletPubkey}/${s.outcomeIndex.toString()}`;
      const params = new URLSearchParams();
      if (s.cacheVersion !== undefined) params.set('v', s.cacheVersion);
      if (s.closed === true) params.set('closed', '1');
      const query = params.toString();
      return query.length === 0 ? path : `${path}?${query}`;
    },
    cache: 'live',
    shellVariant: 'data',
    altKey: 'og.alt.pnl_position',
  },
  pnl_market: {
    kind: 'pnl_market',
    // 2-segment route. Coexists with the 3-segment `pnl_position` route
    // — Next picks based on URL match (no `[outcome]` segment present).
    buildPath: (s: Extract<ShareSurface, { kind: 'pnl_market' }>): string | null => {
      if (s.marketPubkey.length === 0 || s.walletPubkey.length === 0) return null;
      return `/api/og/pnl/${s.marketPubkey}/${s.walletPubkey}`;
    },
    cache: 'live',
    shellVariant: 'data',
    altKey: 'og.alt.pnl_market',
  },
  static_page: {
    kind: 'static_page',
    buildPath: (s: Extract<ShareSurface, { kind: 'static_page' }>): string =>
      `/api/og/static_page/${s.slug}`,
    cache: 'static',
    shellVariant: 'mono',
    altKey: 'og.alt.static_page',
  },
  rewards_rank: {
    kind: 'rewards_rank',
    // Standing is a snapshot encoded in the query (handle/rank/points/level)
    // — each value is its own cache key, so X unfurls the honest rank the
    // user held at share time (no leaderboard drift during the cache window).
    buildPath: (s: Extract<ShareSurface, { kind: 'rewards_rank' }>): string =>
      `/api/og/rewards_rank?handle=${encodeURIComponent(s.handle)}&rank=${s.rank}&points=${encodeURIComponent(s.points)}&level=${encodeURIComponent(s.levelKey)}&name=${encodeURIComponent(s.levelName)}&avatar=${encodeURIComponent(s.avatar ?? '')}`,
    cache: 'live',
    shellVariant: 'data',
    altKey: 'og.alt.rewards_rank',
  },
  market_prize_pool: {
    kind: 'market_prize_pool',
    // Prize pool amount is encoded in the query (`?p=<d6>`) — there is
    // no dedicated SDK endpoint for the live pool value, and the value
    // is already in scope at share time (read off the same market data
    // the page is rendering).
    buildPath: (s: Extract<ShareSurface, { kind: 'market_prize_pool' }>): string | null => {
      if (s.marketPubkey.length === 0) return null;
      const qs = new URLSearchParams({ p: s.prizePoolD6 });
      return `/api/og/market_prize_pool/${s.marketPubkey}?${qs.toString()}`;
    },
    cache: 'live',
    shellVariant: 'celebration',
    altKey: 'og.alt.market_prize_pool',
  },
  prize_claim: {
    kind: 'prize_claim',
    // Claim amount is encoded in the query (`?a=<d6>`) — no
    // `getClaim(market, user)` SDK endpoint exists today, and the
    // amount is already in scope at share time (we computed it from
    // `userLeaf.balance` to issue the on-chain claim). Same query-param
    // pattern as `trade` and `proposal_back`.
    buildPath: (s: Extract<ShareSurface, { kind: 'prize_claim' }>): string | null => {
      if (s.marketPubkey.length === 0 || s.walletPubkey.length === 0) return null;
      const qs = new URLSearchParams({ a: s.amountD6 });
      return `/api/og/claim/${s.marketPubkey}/${s.walletPubkey}?${qs.toString()}`;
    },
    // Claim amount is final once the on-chain tx confirms — never
    // changes — so the OG image is safe to cache forever.
    cache: 'immutable',
    shellVariant: 'celebration',
    altKey: 'og.alt.prize_claim',
  },
  market_result: {
    kind: 'market_result',
    // Per-wallet market resolution hero. 2-segment route, no query
    // params — the OG route fetches winner + holdings server-side from
    // the path segments. Mirrors `pnl_market`'s 2-segment pattern.
    buildPath: (s: Extract<ShareSurface, { kind: 'market_result' }>): string | null => {
      if (s.marketPubkey.length === 0 || s.walletPubkey.length === 0) return null;
      return `/api/og/market_result/${s.marketPubkey}/${s.walletPubkey}`;
    },
    // Market status can still advance (resolved → graduated) and prize
    // distribution can change, so the card is not safe to cache immutably.
    // Same cadence as `pnl_market`.
    cache: 'live',
    shellVariant: 'celebration',
    altKey: 'og.alt.market_result',
  },
  market_result_public: {
    kind: 'market_result_public',
    // Public, wallet-free resolution result card. 1-segment route — the OG
    // route fetches winner + final odds + prize pool + total volume from the
    // market pubkey alone. The shareable "answer revealed" hero (MOR-227).
    buildPath: (s: Extract<ShareSurface, { kind: 'market_result_public' }>): string | null =>
      s.marketPubkey.length > 0 ? `/api/og/market_result_public/${s.marketPubkey}` : null,
    // Prize pool grows until distribution and status can advance
    // (resolved → graduated), so the card is not safe to cache immutably.
    cache: 'live',
    shellVariant: 'celebration',
    altKey: 'og.alt.market_result_public',
  },
};
